import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { whatsappLinks } from "@/lib/whatsapp";

const PREFILLS = {
  "iphone-18-pro": {
    subject: "Anfrage iPhone 18 Pro",
    message:
      "Hallo Haus des Handys,\n\nich interessiere mich für das iPhone 18 Pro.\nFarbe: \nSpeicher: \n\nIst es verfügbar? Bitte informiert mich auch über die Finanzierungsmöglichkeiten (Raten).",
  },
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const prefill = PREFILLS[searchParams.get("anfrage")] || {};
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: prefill.subject || "",
    message: prefill.message || "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: (form.subject || "Kontaktanfrage").trim(),
        message: form.message.trim(),
        user_agent: (typeof navigator !== "undefined" ? navigator.userAgent : "") || "",
      });
      if (data?.ok) {
        setSent(true);
        toast.success("Nachricht gesendet — wir melden uns innerhalb 24h!");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        toast.error(data?.error || "Konnte Nachricht nicht senden");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Konnte Nachricht nicht senden";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-32 pb-24 bg-[#0A0A0B] min-h-screen" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Kontakt</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-white mt-3 leading-[1.02]">
            Sagen Sie Hallo.<br/>
            <span className="text-zinc-500">Wir freuen uns.</span>
          </h1>
        </div>

        <div className="mt-16 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5 space-y-4">
            <ContactCard icon={MapPin} title="Standort" data-testid="contact-location">
              <p className="text-zinc-300">Hernalser Hauptstraße 135</p>
              <p className="text-zinc-300">1170 Wien, Österreich</p>
              <a href="https://maps.google.com/?q=Hernalser+Hauptstrasse+135+Wien" target="_blank" rel="noreferrer" className="text-[#FF2D40] hover:underline text-sm mt-2 inline-block">In Karten öffnen →</a>
            </ContactCard>
            <ContactCard icon={Phone} title="Telefon" data-testid="contact-phone">
              <a href="tel:+436764440304" className="text-zinc-300 hover:text-[#FF2D40]">+43 676 4440304</a>
              <p className="text-xs text-zinc-500 mt-1">Mo–Fr 09:00 – 19:00</p>
            </ContactCard>
            <ContactCard icon={Mail} title="E-Mail" data-testid="contact-email">
              <a href="mailto:store@hausdeshandys.at" className="text-zinc-300 hover:text-[#FF2D40]">store@hausdeshandys.at</a>
            </ContactCard>
            <ContactCard icon={Clock} title="Öffnungszeiten" data-testid="contact-hours">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Mo – Fr</span><span className="font-medium text-white">09:00 – 19:00</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Mittagspause</span><span className="font-medium text-zinc-500">12:30 – 13:00</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Samstag</span><span className="font-medium text-white">09:00 – 16:00</span></div>
                <div className="flex justify-between text-zinc-600"><span>Sonntag</span><span>Geschlossen</span></div>
              </div>
            </ContactCard>
            <a href={whatsappLinks.generic().href} onClick={whatsappLinks.generic().onClick} target="_blank" rel="noreferrer" className="block p-5 rounded-2xl bg-[#25D366] text-white hover:bg-[#1ebd5b] transition-colors" data-testid="contact-whatsapp">
              <div className="flex items-center gap-3">
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current"><path d="M17.498 14.382c-.301-.15-1.767-.872-2.04-.972-.273-.1-.472-.15-.672.15-.197.298-.771.97-.944 1.17-.174.2-.347.226-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.501.099-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.18 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.225-.57-.345m-5.446 7.443h-.016a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <div>
                  <div className="font-display font-semibold">WhatsApp Chat</div>
                  <div className="text-sm opacity-80">Schnellste Antwort</div>
                </div>
              </div>
            </a>
            <a href="https://instagram.com/hausdeshandys" target="_blank" rel="noreferrer" className="flex items-center gap-3 p-5 rounded-2xl bg-[#141417] border border-white/5 text-white hover:border-white/15 transition-colors" data-testid="contact-instagram">
              <Instagram className="w-6 h-6 text-[#FF2D40]" />
              <div>
                <div className="font-display font-semibold">@hausdeshandys</div>
                <div className="text-sm text-zinc-400">Folge uns auf Instagram</div>
              </div>
            </a>
          </div>

          <div className="md:col-span-7 space-y-6">
            <div className="rounded-3xl overflow-hidden border border-white/5 aspect-[16/10] bg-[#141417] relative">
              <iframe
                title="Karte"
                src="https://www.google.com/maps?q=Hernalser+Hauptstrasse+135,+1170+Wien&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
                data-testid="contact-map"
                style={{ filter: "invert(0.92) hue-rotate(180deg)" }}
              ></iframe>
            </div>

            <form onSubmit={submit} className="p-8 rounded-3xl border border-white/5 bg-[#141417] space-y-4" data-testid="contact-form">
              <h2 className="font-display text-2xl font-semibold text-white">Schreiben Sie uns</h2>

              {sent && (
                <div
                  className="p-3.5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-sm text-emerald-200 flex items-start gap-2"
                  data-testid="contact-success"
                >
                  <svg viewBox="0 0 20 20" className="w-4 h-4 mt-0.5 fill-current text-emerald-300 shrink-0"><path d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z"/></svg>
                  <div>
                    Danke! Wir haben deine Nachricht erhalten und melden uns spätestens innerhalb von 24 Stunden.
                    Eine Bestätigung wurde an deine E-Mail gesendet.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0B] border-white/10 text-white" data-testid="contact-name-input" required />
                </div>
                <div>
                  <Label className="text-zinc-400">E-Mail *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0B] border-white/10 text-white" data-testid="contact-email-input" required />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Telefon</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0B] border-white/10 text-white" data-testid="contact-phone-input" />
                </div>
                <div>
                  <Label className="text-zinc-400">Betreff</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="z. B. Reparatur, Ankauf, Zubehör" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="contact-subject-input" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">Nachricht *</Label>
                <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Wobei können wir helfen?" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="contact-message-input" required />
              </div>
              <Button type="submit" disabled={sending} className="w-full bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 btn-lift" data-testid="contact-submit-btn">
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird gesendet…</> : <><Send className="w-4 h-4 mr-2" /> Nachricht senden</>}
              </Button>
              <p className="text-[11px] text-zinc-600 text-center">
                Mit dem Absenden akzeptieren Sie unsere Datenschutzhinweise. Wir antworten meist innerhalb weniger Stunden.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactCard({ icon: Icon, title, children, ...rest }) {
  return (
    <div className="p-5 rounded-2xl border border-white/5 bg-[#141417] flex gap-4" {...rest}>
      <div className="w-10 h-10 rounded-xl bg-[#DC1928] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="text-[15px] text-zinc-300 leading-relaxed">
        <div className="font-display font-semibold mb-1 text-white">{title}</div>
        {children}
      </div>
    </div>
  );
}
